import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import GlobalBluetoothWrapper from '../utils/GlobalBluetoothWrapper';

// Types
interface BluetoothConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  deviceName: string | null;
  deviceId: string | null;
  error: string | null;
}

interface BluetoothCalibrationState {
  isCalibrated: boolean;
  isCalibrating: boolean;
  userWeight: number;
  addedWeights: number;
  error: string | null;
}

interface BluetoothDataStreamState {
  isDataStreamActive: boolean;
}

interface BluetoothState {
  connection: BluetoothConnectionState;
  calibration: BluetoothCalibrationState;
  dataStream: BluetoothDataStreamState;
}

interface BluetoothContextType {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  deviceName: string | null;
  deviceId: string | null;
  connectionError: string | null;
  
  // Calibration state
  isCalibrated: boolean;
  isCalibrating: boolean;
  userWeight: number;
  addedWeights: number;
  calibrationError: string | null;
  
  // Data stream state
  isDataStreamActive: boolean;
  
  // Actions
  connect: () => Promise<void>;
  disconnect: () => void;
  startCalibration: () => void;
  finishCalibration: (weight: number, addedWeights: number) => void;
  resetCalibration: () => void;
  requireNewCalibration: () => void;
  setConnectionError: (error: string | null) => void;
  setCalibrationError: (error: string | null) => void;
  ensureDataStreamActive: (callback: (data: any) => void) => Promise<void>;
}

// Action types
type BluetoothAction =
  | { type: 'SET_CONNECTING'; payload: boolean }
  | { type: 'SET_CONNECTED'; payload: { connected: boolean; deviceName?: string; deviceId?: string } }
  | { type: 'SET_CONNECTION_ERROR'; payload: string | null }
  | { type: 'SET_CALIBRATING'; payload: boolean }
  | { type: 'SET_CALIBRATED'; payload: { calibrated: boolean; weight?: number; addedWeights?: number } }
  | { type: 'SET_CALIBRATION_ERROR'; payload: string | null }
  | { type: 'RESET_CALIBRATION' }
  | { type: 'SET_DATA_STREAM_ACTIVE'; payload: boolean };

// Initial state
const initialState: BluetoothState = {
  connection: {
    isConnected: false,
    isConnecting: false,
    deviceName: null,
    deviceId: null,
    error: null,
  },
  calibration: {
    isCalibrated: false,
    isCalibrating: false,
    userWeight: 0,
    addedWeights: 0,
    error: null,
  },
  dataStream: {
    isDataStreamActive: false,
  },
};

// Reducer
const bluetoothReducer = (state: BluetoothState, action: BluetoothAction): BluetoothState => {
  switch (action.type) {
    case 'SET_CONNECTING':
      return {
        ...state,
        connection: {
          ...state.connection,
          isConnecting: action.payload,
          error: action.payload ? null : state.connection.error,
        },
      };
    case 'SET_CONNECTED':
      return {
        ...state,
        connection: {
          ...state.connection,
          isConnected: action.payload.connected,
          isConnecting: false,
          deviceName: action.payload.deviceName || state.connection.deviceName,
          deviceId: action.payload.deviceId || state.connection.deviceId,
          error: action.payload.connected ? null : state.connection.error,
        },
      };
    case 'SET_CONNECTION_ERROR':
      return {
        ...state,
        connection: {
          ...state.connection,
          error: action.payload,
          isConnecting: false,
        },
      };
    case 'SET_CALIBRATING':
      return {
        ...state,
        calibration: {
          ...state.calibration,
          isCalibrating: action.payload,
          error: action.payload ? null : state.calibration.error,
        },
      };
    case 'SET_CALIBRATED':
      return {
        ...state,
        calibration: {
          ...state.calibration,
          isCalibrated: action.payload.calibrated,
          isCalibrating: false,
          userWeight: action.payload.weight || state.calibration.userWeight,
          addedWeights: action.payload.addedWeights || state.calibration.addedWeights,
          error: action.payload.calibrated ? null : state.calibration.error,
        },
      };
    case 'SET_CALIBRATION_ERROR':
      return {
        ...state,
        calibration: {
          ...state.calibration,
          error: action.payload,
          isCalibrating: false,
        },
      };
    case 'RESET_CALIBRATION':
      return {
        ...state,
        calibration: {
          ...state.calibration,
          isCalibrated: false,
          isCalibrating: false,
          userWeight: 0,
          addedWeights: 0,
          error: null,
        },
      };
    case 'SET_DATA_STREAM_ACTIVE':
      return {
        ...state,
        dataStream: {
          ...state.dataStream,
          isDataStreamActive: action.payload,
        },
      };
    default:
      return state;
  }
};

// Create context
const BluetoothContext = createContext<BluetoothContextType | undefined>(undefined);

// Provider component
interface BluetoothProviderProps {
  children: ReactNode;
}

const BluetoothProvider: React.FC<BluetoothProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(bluetoothReducer, initialState);

  // Set up callbacks to sync with GlobalBluetoothWrapper
  useEffect(() => {
    const wrapper = GlobalBluetoothWrapper.getInstance();
    
    // Set up connection callback
    wrapper.setCallbacks({
      onConnectionChange: (connected, deviceName, deviceId) => {
        dispatch({ 
          type: 'SET_CONNECTED', 
          payload: { 
            connected, 
            deviceName, 
            deviceId 
          } 
        });
        
        // Ensure data stream is started when connected
        if (connected) {
          console.log('BluetoothContext: Device connected, ensuring data stream is active');
          // The data stream will be started by the components that need it
          // This ensures the wrapper is ready for data streaming
        } else {
          // Device disconnected - reset data stream state and calibration
          console.log('BluetoothContext: Device disconnected, resetting data stream state and calibration');
          dispatch({ type: 'SET_DATA_STREAM_ACTIVE', payload: false });
          dispatch({ type: 'RESET_CALIBRATION' });
        }
      },
      onCalibrationChange: (calibrated, weight, addedWeights) => {
        console.log('BluetoothContext: onCalibrationChange callback received - calibrated:', calibrated, 'weight:', weight, 'addedWeights:', addedWeights);
        dispatch({ 
          type: 'SET_CALIBRATED', 
          payload: { 
            calibrated, 
            weight, 
            addedWeights 
          } 
        });
      },
      onError: (error) => {
        dispatch({ type: 'SET_CONNECTION_ERROR', payload: error });
      }
    });

    // Sync connection state with underlying BluetoothWrapper first
    wrapper.syncConnectionState().then(() => {
      // Initialize state from wrapper after sync
      const connectionState = wrapper.getConnectionState();
      const calibrationState = wrapper.getCalibrationState();
      
      console.log('BluetoothContext: Initial state after sync - connection:', connectionState, 'calibration:', calibrationState);
      
      dispatch({ 
        type: 'SET_CONNECTED', 
        payload: { 
          connected: connectionState.isConnected, 
          deviceName: connectionState.deviceName, 
          deviceId: connectionState.deviceId 
        } 
      });
      
      dispatch({ 
        type: 'SET_CALIBRATED', 
        payload: { 
          calibrated: calibrationState.isCalibrated, 
          weight: calibrationState.userWeight, 
          addedWeights: calibrationState.addedWeights 
        } 
      });
    });
  }, []);

  // Actions
  const connect = useCallback(async (): Promise<void> => {
    dispatch({ type: 'SET_CONNECTING', payload: true });
    try {
      console.log('BluetoothContext: Starting connection...');
      const wrapper = GlobalBluetoothWrapper.getInstance();
      await wrapper.connect();
      // The connection state will be updated via the callback
    } catch (error) {
      dispatch({ type: 'SET_CONNECTION_ERROR', payload: error.message });
    }
  }, [dispatch]);

  const disconnect = useCallback((): void => {
    console.log('BluetoothContext: Disconnecting...');
    const wrapper = GlobalBluetoothWrapper.getInstance();
    wrapper.disconnect();
    // Update data stream state to false when disconnecting
    dispatch({ type: 'SET_DATA_STREAM_ACTIVE', payload: false });
    // The disconnection state will be updated via the callback
  }, [dispatch]);

  const startCalibration = useCallback((): void => {
    console.log('BluetoothContext: Starting calibration...');
    const wrapper = GlobalBluetoothWrapper.getInstance();
    wrapper.startCalibration();
    // The calibration state will be updated via the callback
  }, []);

  const finishCalibration = useCallback((weight: number, addedWeights: number): void => {
    dispatch({ 
      type: 'SET_CALIBRATED', 
      payload: { 
        calibrated: true, 
        weight, 
        addedWeights 
      } 
    });
    console.log('BluetoothContext: Calibration complete:', { weight, addedWeights });
  }, [dispatch]);

  const resetCalibration = useCallback((): void => {
    dispatch({ type: 'RESET_CALIBRATION' });
    console.log('BluetoothContext: Calibration reset');
  }, [dispatch]);

  const requireNewCalibration = useCallback((): void => {
    dispatch({ type: 'RESET_CALIBRATION' });
    console.log('BluetoothContext: New calibration required for exercise');
  }, [dispatch]);

  const setConnectionError = useCallback((error: string | null): void => {
    dispatch({ type: 'SET_CONNECTION_ERROR', payload: error });
  }, [dispatch]);

  const setCalibrationError = useCallback((error: string | null): void => {
    dispatch({ type: 'SET_CALIBRATION_ERROR', payload: error });
  }, [dispatch]);

  const ensureDataStreamActive = useCallback(async (callback: (data: any) => void): Promise<void> => {
    console.log('BluetoothContext: Ensuring data stream is active...');
    const wrapper = GlobalBluetoothWrapper.getInstance();
    await wrapper.ensureDataStreamActive(callback);
    // Update data stream state
    dispatch({ type: 'SET_DATA_STREAM_ACTIVE', payload: true });
  }, [dispatch]);

  const contextValue: BluetoothContextType = {
    // Connection state
    isConnected: state.connection.isConnected,
    isConnecting: state.connection.isConnecting,
    deviceName: state.connection.deviceName,
    deviceId: state.connection.deviceId,
    connectionError: state.connection.error,
    
    // Calibration state
    isCalibrated: state.calibration.isCalibrated,
    isCalibrating: state.calibration.isCalibrating,
    userWeight: state.calibration.userWeight,
    addedWeights: state.calibration.addedWeights,
    calibrationError: state.calibration.error,
    
    // Data stream state
    isDataStreamActive: state.dataStream.isDataStreamActive,
    
    // Actions
    connect,
    disconnect,
    startCalibration,
    finishCalibration,
    resetCalibration,
    requireNewCalibration,
    setConnectionError,
    setCalibrationError,
    ensureDataStreamActive,
  };

  return (
    <BluetoothContext.Provider value={contextValue}>
      {children}
    </BluetoothContext.Provider>
  );
};

// Hook to use the context
export const useBluetooth = (): BluetoothContextType => {
  const context = useContext(BluetoothContext);
  if (context === undefined) {
    throw new Error('useBluetooth must be used within a BluetoothProvider');
  }
  return context;
};

export default BluetoothProvider;
